import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getChartRiskRatios } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Notes } from '../others';
import AssetSelect from '../AssetSelect';
import RiskRatiosBase from './risk-ratios/RiskRatios';
import SingleAssetHeader from 'views/SingleAssetHeader';
import ToggleContainer from 'components/ToggleContainer';

function RiskRatios({ session }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ product: '', database: '', ratio: '', window: '12', converter: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getChartRiskRatios(options);

    setResult(data);
    setError(err);

    setLoading(false);
  };

  useEffect(() => {
    if (!options.product) return;
    fetchData();
  }, [options]);

  let main = null;
  if (loading) {
    main = <FixedCenterLoader />;
  } else if (result) {
    const chartData = result.chart[options.ratio];
    const tableData = result.table[options.ratio];

    main = (
      <>
        <SingleAssetHeader data={result.header} />
        <Descriptions content={result.description} />
        <RiskRatiosBase data={{ chart: chartData, table: tableData }} params={options} />
        <Notes content={result.notes} />
      </>
    );
  } else if (error) {
    main = (
      <>
        <div className="tw-p-4 tw-mb-4 tw-text-sm tw-text-blue-800 tw-rounded-lg tw-bg-blue-100" role="alert">
          <span className="tw-font-bold">{error}</span>
        </div>
      </>
    );
  }

  const sideMenu = (
    <AssetSelect
      session={session}
      onSelect={({ database, product, ratio, period, converter }) => {
        setOptions({ database, product, ratio, window: period, converter });
      }}
      onAllowed={({ database, product, ratio, period, converter }) =>
        !!(database && product && ratio && period && converter)
      }
      enableRatio
      enablePeriod
    />
  );

  const mainContent = <div key={options.product}>{main}</div>;

  return (
    <ToggleContainer
      sideMenu={sideMenu}
      mainContent={options.product && mainContent}
      classNames={{ sideMenu: 'tw-w-[400px]' }}
    />
  );
}

export default RiskRatios;
